import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type { FilterModel, IServerSideGetRowsParams } from 'ag-grid-community';

import { useFeatureFlag } from '@/app/services/features';
import { useAccessTokenRef } from '@/services/auth';
import { shipmentGridQueryOptions } from '@/services/shipping';
import { addGridQueryParams } from '@/utils/query';

import { CONTENT_TYPE_FIELDS } from '../utils/columnDefs';

const OR_FLAG = 'shipping_or_content_filters';

export function useRestDataSource({
  quickFilterText,
  selectedContentTypeFields,
}: {
  quickFilterText?: string;
  selectedContentTypeFields?: string[];
}) {
  const tokenRef = useAccessTokenRef();
  const queryClient = useQueryClient();
  const useOrFilters = useFeatureFlag(OR_FLAG);

  const getRows = useCallback(
    ({ request, success, fail }: IServerSideGetRowsParams) => {
      if (
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        return;
      }

      const baseParams = new URLSearchParams();
      if (quickFilterText) baseParams.set('search', quickFilterText);

      let filterModel = request.filterModel as FilterModel | null;

      if (useOrFilters) {
        const allSelected =
          !selectedContentTypeFields ||
          selectedContentTypeFields.length === 0 ||
          selectedContentTypeFields.length === CONTENT_TYPE_FIELDS.length;

        if (!allSelected) {
          const orFilter = {
            $or: selectedContentTypeFields!.map((field) => ({
              [field]: { $eq: true },
            })),
          };
          baseParams.set('$filter', JSON.stringify(orFilter));
        }

        if (filterModel) {
          const cleaned: FilterModel = {};
          for (const [key, value] of Object.entries(filterModel)) {
            if (!CONTENT_TYPE_FIELDS.includes(key)) {
              cleaned[key] = value;
            }
          }
          filterModel = Object.keys(cleaned).length > 0 ? cleaned : null;
        }
      } else {
        if (selectedContentTypeFields?.length) {
          for (const field of selectedContentTypeFields) {
            baseParams.set(field, 'true');
          }
        }
      }

      const searchParams = addGridQueryParams(
        baseParams,
        filterModel,
        request.sortModel,
        request.startRow,
        request.endRow,
      );

      queryClient
        .fetchQuery(shipmentGridQueryOptions(tokenRef.current, searchParams))
        .then((result) => {
          success({
            rowData: result.items,
            rowCount: result.total,
          });
        })
        .catch((error) => {
          console.error('Shipping grid query failed', error);
          fail();
        });
    },
    [
      tokenRef,
      queryClient,
      quickFilterText,
      selectedContentTypeFields,
      useOrFilters,
    ],
  );

  return {
    dataSource: useMemo(() => ({ getRows }), [getRows]) as {
      getRows: (params: IServerSideGetRowsParams) => void;
    },
  };
}
